<template>
  <div class="map-container pt-1 pb-0">
    <!-- Continent quick-select buttons -->
    <div class="flex flex-nowrap gap-1 justify-center mb-1 px-1 overflow-x-auto scrollbar-hide">
      <button
        v-for="continent in continents"
        :key="continent.id"
        type="button"
        :class="[
          'flex-shrink-0 px-1.5 py-0.5 text-[10px] sm:text-xs font-medium rounded-md transition-all duration-200 text-white hover:shadow-md border focus:outline-none',
          selectedContinents.includes(continent.id)
            ? 'bg-gradient-to-b from-old-map-green1 to-old-map-green2'
            : 'bg-gradient-to-b from-old-map-brown1 to-old-map-brown2'
        ]"
        @click="handleContinentClick(continent.id)"
      >
        {{ continent.name }}
      </button>
    </div>

    <!-- Map -->
    <div id="map" ref="mapRef" class="w-full h-auto" @click="countryUpdate"></div>

    <!-- Live selection summary — animated horizontal scroll row -->
    <div class="chip-bar px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-100 overflow-hidden w-full">
      <span v-if="liveSelection.length === 0" class="text-xs text-gray-400 italic px-1">
        {{ t('map.clickPrompt') }}
      </span>
      <div v-else ref="chipRow" class="chip-row">
        <TransitionGroup name="chip" tag="div" class="chip-inner">
          <span
            v-for="item in liveSelection"
            :key="item.type + '-' + (item.id || item.code)"
            class="chip-item flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium flex-shrink-0"
          >
            {{ item.display }}
            <button
              type="button"
              class="text-green-400 hover:text-green-700 focus:outline-none leading-none ml-0.5"
              :aria-label="'Remove ' + item.display"
              @click="removeSelection(item)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        </TransitionGroup>
      </div>
    </div>

    <!-- Footer: Explore link -->
    <div class="flex justify-center pt-1 pb-0.5">
      <router-link
        to="/explore"
        class="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {{ t('map.explorePopular') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, onUnmounted, nextTick } from "vue";
import { useI18n } from 'vue-i18n';
import {useScreenSize} from "@/composables/useScreenSize";
import eventBus from '@/eventBus'

const { t } = useI18n();

const { isLargeScreen } = useScreenSize();
const selectedContinents = ref([]);
const mapRef = ref(null);

// Live selection summary — updated whenever the map emits 'countries-updated'
const liveSelection = ref([]);
const chipRow = ref(null);
// Preserve insertion order so newest chip always goes to the end
const chipOrder = ref([]);

const continents = [
{ id: 'europe', name: 'Europe' },
{ id: 'namerica', name: 'North America' },
{ id: 'asia', name: 'Asia' },
{ id: 'samerica', name: 'South America' },
{ id: 'oceania', name: 'Oceania' },
{ id: 'africa', name: 'Africa' },
];

const continentToRegionMap = {
  'europe': 2,
  'asia': 4,
  'africa': 3,
  'namerica': 0,
  'samerica': 1,
  'oceania': 5,
};

const getSelectionByContinent = () => {
  if (!window.simplemaps_select?.selected) return {};
  
  const groupedCountries = {};
  continents.forEach(continent => {
    const regionIndex = continentToRegionMap[continent.id];
    const continentStates = window.simplemaps_worldmap_mapdata.regions[regionIndex].states;
    const selectedStates = continentStates.filter(state => 
      window.simplemaps_select.selected.includes(state)
    );
    
    groupedCountries[continent.id] = {
      total: continentStates.length,
      selected: selectedStates.length,
      states: selectedStates
    };
  });
  
  return groupedCountries;
};

const handleContinentClick = (continentId) => {
  if (!window.simplemaps_worldmap || !window.simplemaps_select) return;
  
  const regionIndex = continentToRegionMap[continentId];
  const states = window.simplemaps_worldmap_mapdata.regions[regionIndex].states;
  const isSelected = !selectedContinents.value.includes(continentId);

  if (isSelected) {
    states.forEach(stateId => window.simplemaps_select.select(stateId));
    selectedContinents.value.push(continentId);
  } else {
    states.forEach(stateId => window.simplemaps_select.deselect(stateId));
    selectedContinents.value = selectedContinents.value.filter(id => id !== continentId);
  }
  
  countryUpdate();
};

// Map initialization code
const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};


const initializeMap = () => {
  if (!window.simplemaps_worldmap) return;

  const multiplier = ref(1);

  if (isLargeScreen?.value) {
    multiplier.value = 0.66;
  } else {
    multiplier.value = 1;
  }

  const mapContainerElement = mapRef.value;
  if (!mapContainerElement?.isConnected) return;
  const parentElementHasSize = mapContainerElement.parentElement?.clientWidth > 0;
  const screenWidth = parentElementHasSize ? mapContainerElement.parentElement.clientWidth : window.innerWidth * multiplier.value;
  
  const mapWidth = screenWidth;
  const mapHeight = mapWidth * 0.5;

  window.simplemaps_worldmap_mapdata.main_settings = {
    ...window.simplemaps_worldmap_mapdata.main_settings,
    width: Math.round(mapWidth).toString(),
    height: Math.round(mapHeight).toString(),
    responsive: 'yes',
    initial_zoom: isLargeScreen?.value ? -1 : 2
  };

  console.log('Initial map settings 2:', window.simplemaps_worldmap_mapdata.main_settings);
  window.simplemaps_worldmap.load();

  // Register hook AFTER load() so plugin_hooks.click_state is populated
  if (window.simplemaps_worldmap.plugin_hooks?.click_state) {
    window.simplemaps_worldmap.plugin_hooks.click_state.push(() => countryUpdate());
  }
};

const countryUpdate = () => {
  setTimeout(() => {
    // const selectedCountries = window.simplemaps_select.selected;
    const optimizedSelection = [];
    
    // Check each continent
    Object.entries(getSelectionByContinent()).forEach(([continentId, data]) => {
      if (data.selected === data.total) {
        // If all countries in continent are selected, just add the continent
        optimizedSelection.push({
          type: 'continent',
          id: continentId,
          name: continents.find(c => c.id === continentId)?.name
        });
      } else if (data.selected > 0) {
        // If only some countries are selected, add them individually
        data.states.forEach(countryCode => {
          optimizedSelection.push({
            type: 'country',
            code: countryCode,
            name: window.simplemaps_worldmap_mapdata.state_specific[countryCode]?.name
          });
        });
      }
    });

    // Update live summary chips in-map, preserving insertion order
    const mapped = optimizedSelection.map(item => ({
      ...item,
      display: item.type === 'continent' ? `All ${item.name}` : item.name
    }));
    const newKeys = new Set(mapped.map(i => i.type + '-' + (i.id || i.code)));
    // Remove keys that are no longer selected
    chipOrder.value = chipOrder.value.filter(k => newKeys.has(k));
    // Append any newly added keys at the end
    mapped.forEach(i => {
      const k = i.type + '-' + (i.id || i.code);
      if (!chipOrder.value.includes(k)) chipOrder.value.push(k);
    });
    // Sort by insertion order
    const orderMap = Object.fromEntries(chipOrder.value.map((k, idx) => [k, idx]));
    liveSelection.value = [...mapped].sort((a, b) => {
      const ka = a.type + '-' + (a.id || a.code);
      const kb = b.type + '-' + (b.id || b.code);
      return (orderMap[ka] ?? 999) - (orderMap[kb] ?? 999);
    });
    // Scroll chip row to end so newest item is visible
    nextTick(() => {
      if (chipRow.value) chipRow.value.scrollLeft = chipRow.value.scrollWidth;
    });

    console.log('Emitting optimized selection:', liveSelection.value);
    eventBus.emit('countries-updated', [...liveSelection.value]);
  }, 50);
};

// Remove a single item from the selection (deselects on the map)
const removeSelection = (item) => {
  if (!window.simplemaps_select) return;
  if (item.type === 'continent') {
    const regionIndex = continentToRegionMap[item.id];
    const states = window.simplemaps_worldmap_mapdata?.regions?.[regionIndex]?.states || [];
    states.forEach(stateId => window.simplemaps_select.deselect(stateId));
    selectedContinents.value = selectedContinents.value.filter(id => id !== item.id);
  } else if (item.code) {
    window.simplemaps_select.deselect(item.code);
    // Check if parent continent was fully selected and mark it as partial now
    for (const [continentId, regionIndex] of Object.entries(continentToRegionMap)) {
      const states = window.simplemaps_worldmap_mapdata?.regions?.[regionIndex]?.states || [];
      if (states.includes(item.code)) {
        selectedContinents.value = selectedContinents.value.filter(id => id !== continentId);
        break;
      }
    }
  }
  countryUpdate();
};

const handleResize = () => {
  // Check if map and its dependencies are fully loaded
  if (!window.simplemaps_worldmap || !window.simplemaps_worldmap_mapdata || !mapRef.value?.isConnected) return;

  const screenWidth = mapRef.value.parentElement?.clientWidth || window.innerWidth;

  const mapWidth = screenWidth;
  const mapHeight = mapWidth * 0.5;

  window.simplemaps_worldmap_mapdata.main_settings = {
    ...window.simplemaps_worldmap_mapdata.main_settings,
    width: Math.round(mapWidth).toString(),
    height: Math.round(mapHeight).toString(),
    responsive: 'yes',
    initial_zoom: isLargeScreen?.value ? -1 : 2
  };

  console.log('Map settings:', window.simplemaps_worldmap_mapdata.main_settings);
  window.simplemaps_worldmap.load();
};

onMounted(async () => {
  try {
    await loadScript('/mapdata.js');
    await loadScript('/worldmap.js');
    await loadScript('/select.js');
    
    if (window.simplemaps_worldmap) {
      window.simplemaps_worldmap_mapdata.main_settings.all_states_zoomable = 'no';
      window.simplemaps_worldmap_mapdata.main_settings.state_url = '';
      window.simplemaps_worldmap_mapdata.main_settings.back_image = 'no';
      
      window.simplemaps_select.map = window.simplemaps_worldmap;
      window.simplemaps_select.selected_color = '#A6AA8A';

      window.addEventListener('resize', handleResize);
      
      initializeMap(); // Call initializeMap to ensure map is sized correctly on first load

      // Sync the live chip bar with any selections the parent replayed onto the map
      // (e.g. individual countries re-selected via simplemaps_select.select() on reopen)
      setTimeout(() => countryUpdate(), 200);
    }
  } catch (error) {
    console.error('Error loading map:', error);
  } 

  eventBus.on('continent-deselected-from-field', (continentId) => {
    selectedContinents.value = selectedContinents.value.filter(id => id !== continentId);
    // Sync live summary after external deselection
    setTimeout(() => countryUpdate(), 60);
  });

  eventBus.on('continent-selected-from-field', (continentId) => {
    if (!selectedContinents.value.includes(continentId)) {
      selectedContinents.value.push(continentId);
    }
    // Sync live summary after external selection
    setTimeout(() => countryUpdate(), 60);
  });

});

onUnmounted(() => { 
  window.removeEventListener('resize', handleResize);
  eventBus.off('continent-deselected-from-field');
  eventBus.off('continent-selected-from-field');
});

</script>

<style scoped>
.map-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

#map {
  width: 100%;
}

/* Hide all simplemaps built-in controls: zoom, back arrow, Navigate/States/Regions dropdowns */
:deep(#map select),
:deep(#map img[src*='back']),
:deep(#map > div > img),
:deep(.smzoom),
:deep(.sm_zoom_in),
:deep(.sm_zoom_out),
:deep([id*='zoom']) {
  display: none !important;
}

/* Chip scroll row */
.chip-bar {
  min-height: 2.25rem;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.chip-row {
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  width: 0;
  min-width: 100%;
}
.chip-row::-webkit-scrollbar {
  display: none;
}
.chip-inner {
  display: flex;
  gap: 0.375rem;
  align-items: center;
  width: max-content;
  min-width: 100%;
}

/* Chip enter animation — slides in from right, pushes others left */
.chip-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.chip-leave-active {
  transition: all 0.18s ease-in;
  position: absolute;
}
.chip-move {
  transition: transform 0.25s ease;
}
.chip-enter-from {
  opacity: 0;
  transform: translateX(20px) scale(0.85);
}
.chip-leave-to {
  opacity: 0;
  transform: scale(0.75);
}
</style>
